import { useId, useMemo, useState } from "react";
import { neighbors, offerGraph } from "./graph";

type Props = {
  compact?: boolean;
};

export function NetworkAnalysisGraph({ compact = false }: Props) {
  const titleId = useId();
  const descId = useId();
  const [active, setActive] = useState(offerGraph.nodes[0].id);
  const selected = offerGraph.nodes.find((node) => node.id === active) ?? offerGraph.nodes[0];
  const related = useMemo(() => neighbors(selected.id), [selected.id]);

  return (
    <div className="graph-wrap">
      <figure>
        <svg
          className="graph-svg"
          viewBox="0 0 560 400"
          role="img"
          aria-labelledby={titleId}
          aria-describedby={descId}
        >
          <title id={titleId}>{offerGraph.title}</title>
          <desc id={descId}>{offerGraph.description}</desc>
          {offerGraph.edges.map((edge) => {
            const from = offerGraph.nodes.find((node) => node.id === edge.from);
            const to = offerGraph.nodes.find((node) => node.id === edge.to);
            if (!from || !to) return null;
            const highlighted =
              edge.from === selected.id || edge.to === selected.id;
            return (
              <line
                key={`${edge.from}-${edge.to}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={highlighted ? "var(--signal-bright)" : "var(--line)"}
                strokeWidth={1.5 + edge.weight * 4}
                strokeOpacity={highlighted ? 1 : 0.85}
              />
            );
          })}
          {offerGraph.nodes.map((node) => {
            const radius = 10 + node.score * 18;
            const isActive = node.id === selected.id;
            const fill =
              node.role === "core"
                ? "var(--human)"
                : isActive
                  ? "var(--signal-bright)"
                  : "var(--signal)";
            return (
              <g key={node.id}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={radius}
                  fill={fill}
                  stroke={isActive ? "var(--ink)" : "var(--paper-raised)"}
                  strokeWidth={isActive ? 3 : 2}
                  style={{ cursor: "pointer" }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${node.label}, centrality ${node.score.toFixed(2)}`}
                  onClick={() => setActive(node.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActive(node.id);
                    }
                  }}
                />
                <text
                  x={node.x}
                  y={node.y + radius + 16}
                  textAnchor="middle"
                  fontSize={compact ? 11 : 12}
                  fontFamily="IBM Plex Sans, sans-serif"
                  fill="var(--ink)"
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>
        <figcaption className="meta" style={{ marginTop: "0.75rem" }}>
          {offerGraph.description} Color is not the only signal: radius encodes
          centrality, and the table lists neighbors.
        </figcaption>
      </figure>
      <div>
        <h3 id={`node-${selected.id}`}>{selected.label}</h3>
        <p className="meta">
          Centrality {selected.score.toFixed(2)} · {selected.role} node
        </p>
        <p>
          Neighbors are ordered by edge weight from co-occurrence and
          acceptance. Sparse links (wallet–insurance) are in fallback
          territory.
        </p>
        <table className="graph-table">
          <caption className="visually-hidden">
            Nodes, centrality scores, and neighbors for the synthetic offer
            graph
          </caption>
          <thead>
            <tr>
              <th scope="col">Neighbor</th>
              <th scope="col">Weight</th>
              <th scope="col">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {related.map((item) => (
              <tr key={item.node.id}>
                <td>
                  <button
                    type="button"
                    className="button-secondary"
                    style={{ padding: "0.2rem 0.55rem" }}
                    onClick={() => setActive(item.node.id)}
                  >
                    {item.node.label}
                  </button>
                </td>
                <td>{item.weight.toFixed(2)}</td>
                <td>{item.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!compact ? (
          <table className="graph-table" style={{ marginTop: "1rem" }}>
            <caption className="visually-hidden">All nodes</caption>
            <thead>
              <tr>
                <th scope="col">Offer</th>
                <th scope="col">Score</th>
                <th scope="col">Role</th>
              </tr>
            </thead>
            <tbody>
              {offerGraph.nodes.map((node) => (
                <tr key={node.id}>
                  <td>{node.label}</td>
                  <td>{node.score.toFixed(2)}</td>
                  <td>{node.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <p className="meta">
          Prefer reduced motion? The SVG is static; nothing autoplays.
        </p>
      </div>
    </div>
  );
}
